import { Message } from '@progress/kendo-angular-conversational-ui';
import { uniqBy } from 'lodash';
import { ChatHistoryReplyPreview } from '../model/chat-history-reply-preview';
import { ChatMessageReaction } from '../model/chat-message-reaction';

export interface ChatScrollAnchor
{
  nearBottom: boolean;
  scrollTop: number;
}

export interface ChatCountEntry
{
  chatId: number | string;
  unreadCount: number;
}

export interface MapHistoryMessageContext
{
  statusPlaceholder: string;
  textChatUpdated: string;
  resolveAuthorAvatarUrl: (authorId: number | string, authorName?: string) => string;
  resolveAuthorName?: (authorId: number | string) => string | undefined;
}

export interface HistoryMergeResult
{
  nextHistory: Message[];
  structureChanged: boolean;
  patchedInPlace: boolean;
}

export function chatIdsMatch(left: unknown, right: unknown): boolean
{
  return left != null && right != null && String(left) === String(right);
}

export function getReplyTargetMessageId(message: Message | null | undefined): number | null
{
  const id = message?.id;
  if (id == null)
  {
    return null;
  }

  const numericId = Number(id);
  return Number.isFinite(numericId) ? numericId : null;
}

export function getMessageReplyToId(message: Message | null | undefined): number | null
{
  if (!message)
  {
    return null;
  }

  const extended = message as Message & {
    replyTo?: number | string;
    replyChatHistoryId?: number | string;
  };
  const raw = extended.replyToId ?? extended.replyTo ?? extended.replyChatHistoryId;
  if (raw == null)
  {
    return null;
  }

  const numericId = Number(raw);
  return Number.isFinite(numericId) ? numericId : null;
}

export function getMessageReactions(message: Message): ChatMessageReaction[]
{
  const reactions = (message as Message & { reactions?: ChatMessageReaction[] }).reactions;
  return Array.isArray(reactions) ? reactions : [];
}

export function reactionsEqual(left: ChatMessageReaction[], right: ChatMessageReaction[]): boolean
{
  if (left.length !== right.length)
  {
    return false;
  }

  const key = (reaction: ChatMessageReaction): string =>
    `${reaction.emojiKey}|${reaction.userId}|${reaction.userName ?? ''}`;
  const leftKeys = [...left].map(key).sort();
  const rightKeys = [...right].map(key).sort();
  return leftKeys.every((value, index) => value === rightKeys[index]);
}

export function historyMessageContentEquals(local: Message, server: Message): boolean
{
  const localExt = local as Message & { content?: string; updated?: boolean };
  const serverExt = server as Message & { content?: string; updated?: boolean };
  const localText = local.text ?? localExt.content ?? '';
  const serverText = server.text ?? serverExt.content ?? '';
  return localText === serverText
    && Boolean(localExt.updated) === Boolean(serverExt.updated)
    && getMessageReplyToId(local) === getMessageReplyToId(server)
    && reactionsEqual(getMessageReactions(local), getMessageReactions(server));
}

export function patchHistoryMessage(local: Message, server: Message): void
{
  const target = local as Message & {
    reactions?: ChatMessageReaction[];
    replyToId?: string | number;
    content?: string;
    updated?: boolean;
    status?: Message['status'];
  };
  const serverExt = server as Message & { content?: string; updated?: boolean };
  target.reactions = [...getMessageReactions(server)];
  target.updated = serverExt.updated;
  target.status = server.status;
  target.text = server.text ?? serverExt.content;
  target.content = serverExt.content;
  const replyToId = getMessageReplyToId(server);
  if (replyToId != null)
  {
    target.replyToId = replyToId;
  }

  const serverPreview = (server as Message & { replyToPreview?: ChatHistoryReplyPreview }).replyToPreview;
  if (serverPreview)
  {
    (target as Message & { replyToPreview?: ChatHistoryReplyPreview }).replyToPreview = { ...serverPreview };
  }
}

export function mapHistoryMessage(message: any, context: MapHistoryMessageContext): any
{
  if (message.id != null)
  {
    message.id = Number(message.id);
  }

  message.status = message.updated ? context.textChatUpdated : context.statusPlaceholder;
  message.timestamp = new Date(message.chatDateTime);
  delete message.chatDateTime;
  const resolvedName = context.resolveAuthorName?.(message.author.id);
  message.author.avatarUrl = context.resolveAuthorAvatarUrl(message.author.id, message.author.name);
  if (!message.author.name && resolvedName)
  {
    message.author.name = resolvedName;
  }
  if (message.text === undefined || message.text === null)
  {
    message.text = message.content;
  }

  message.reactions = Array.isArray(message.reactions) ? message.reactions : [];
  const replyToId = getMessageReplyToId(message);
  if (replyToId != null)
  {
    message.replyToId = replyToId;
  }

  if (message.replyToPreview?.id != null)
  {
    message.replyToPreview.id = Number(message.replyToPreview.id);
  }

  return message;
}

export function parseChatCountPayload(stompMessage: { body?: string }): ChatCountEntry[]
{
  try
  {
    const raw = stompMessage?.body;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  }
  catch
  {
    return [];
  }
}

export function computeHistoryMerge(
  persistedHistory: Message[],
  serverMessages: Message[]
): HistoryMergeResult
{
  if (persistedHistory.length === 0)
  {
    return {
      nextHistory: serverMessages,
      structureChanged: true,
      patchedInPlace: false
    };
  }

  const serverById = new Map(serverMessages.map((message) => [String(message.id), message]));
  let patchedInPlace = false;

  for (const message of persistedHistory)
  {
    const updated = serverById.get(String(message.id));
    if (!updated || historyMessageContentEquals(message, updated))
    {
      continue;
    }

    patchHistoryMessage(message, updated);
    patchedInPlace = true;
  }

  const localIds = new Set(persistedHistory.map((message) => String(message.id)));
  const newPosts = serverMessages.filter((message) => !localIds.has(String(message.id)));
  const serverIds = new Set(serverMessages.map((message) => String(message.id)));
  const removedAny = persistedHistory.some((message) =>
    message.id != null && !serverIds.has(String(message.id)));
  const structureChanged = newPosts.length > 0 || removedAny;

  if (structureChanged)
  {
    const withoutDeleted = persistedHistory.filter((message) =>
      message.id == null || serverIds.has(String(message.id)));
    return {
      nextHistory: uniqBy([...withoutDeleted, ...newPosts], (message) => String(message.id)),
      structureChanged: true,
      patchedInPlace
    };
  }

  return {
    nextHistory: persistedHistory,
    structureChanged: false,
    patchedInPlace
  };
}
